import React, { useState, useEffect, useRef } from "react";
import { TextField, Autocomplete, Button, Accordion, AccordionSummary, AccordionDetails, IconButton, Typography, Snackbar, Alert, Tooltip, CircularProgress } from "@mui/material";
import { DataGridPro, GridTreeDataGroupingCell } from '@mui/x-data-grid-pro';
import DefaultToolbar from "src/components/DataGrid/DefaultToolbar";
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ReceiptIcon from '@mui/icons-material/Receipt';
import CloseIcon from '@mui/icons-material/Close';
import { AdapterDayjs } from "@mui/x-date-pickers-pro/AdapterDayjs";
import { DateRangePicker } from "@mui/x-date-pickers-pro/DateRangePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import dayjs from 'dayjs';
import moment from "moment";
import axios from "axios";
import { APIURL } from "../../../config";
import { convertTime } from "src/utils/time";

const shortcutsItems = [
    {
        label: 'This Week',
        getValue: () => {
            const today = dayjs();
            return [today.startOf('week'), today.endOf('week')];
        },
    },
    {
        label: 'Last Week',
        getValue: () => {
            const today = dayjs();
            const prevWeek = today.subtract(7, 'day');
            return [prevWeek.startOf('week'), prevWeek.endOf('week')];
        },
    },
    {
        label: 'Last 7 Days',
        getValue: () => {
            const today = dayjs();
            return [today.subtract(7, 'day'), today];
        },
    },
    {
        label: 'Current Month',
        getValue: () => {
            const today = dayjs();
            return [today.startOf('month'), today.endOf('month')];
        },
    },
    {
        label: 'Last Month',
        getValue: () => {
            const today = dayjs();
            const lastMonth = today.subtract(1, 'month');
            return [lastMonth.startOf('month'), lastMonth.endOf('month')];
        },
    },
];

export default function PrivatePayerBilling(props) {
    const [customerList, setCustomerList] = useState([]);
    const [selectedCustomer, setSelectedCustomer] = useState(null);
    const [fromDate, setFromDate] = useState(() => dayjs().startOf('week').toDate());
    const [toDate, setToDate] = useState(() => dayjs().endOf('week').toDate());
    const [privatePayerTasks, setPrivatePayerTasks] = useState([]);
    const [privatePayerInvoices, setPrivatePayerInvoices] = useState([]);
    const [isAccordion1Open, setIsAccordion1Open] = useState(false);
    const [isAccordion2Open, setIsAccordion2Open] = useState(false);
    const [isGenerating, setIsGenerating] = useState(false);
    const [selectionModel, setSelectionModel] = useState({
        type: 'include',
        ids: new Set(),
    });
    const [isSending, setIsSending] = useState(false);
    const [invoiceSelectionModel, setInvoiceSelectionModel] = useState({
        type: 'include',
        ids: new Set(),
    });
    const [loading, setLoading] = useState(false);
    const [alertMessage, setAlertMessage] = useState('');
    const [alertType, setAlertType] = useState('');

    // Keep the summary the same height when expanded so the grid sits close to the title
    const summarySx = {
        minHeight: 48,
        '&.Mui-expanded': { minHeight: 48 },
        '& .MuiAccordionSummary-content.Mui-expanded': { margin: '12px 0' },
    };

    const detailsSx = { paddingTop: 0 };

    // Compute height based on both accordions being open
    const gridHeight = (isAccordion1Open && isAccordion2Open)
        ? 'calc(100vh - 675px)'
        : 'calc(100vh - 350px)';

    useEffect(() => {
        fetchCustomerData();
        fetchData();
    }, []);

    const fetchCustomerData = async () => {
        axios
            .get(APIURL + "/customers/v2/associated", {
                params: {
                    company_id: props.user.comp_id,
                    employee_id: props.user.emp_id,
                    type: props.user.permissions.AdminLevelCustomers ? "Admin" : "Manager"
                },
            })
            .then((response) => {
                setCustomerList(response?.data.filter((cus) => cus.cus_status === 'Active') || []);
            });
    };

    const fetchData = async () => {
        setLoading(true);

        axios
            .get(APIURL + "/privatePayerBilling/task", {
                params: {
                    compID: props.user.comp_id,
                    custID: selectedCustomer?.cus_id ?? null,
                    fromDate: fromDate ? moment(fromDate).format('YYYY-MM-DD') : null,
                    toDate: toDate ? moment(toDate).format('YYYY-MM-DD') : null,
                },
            })
            .then((response) => {
                setPrivatePayerTasks(response.data);
            })
            .catch((error) => {
                console.log(error);
            });

        axios
            .get(APIURL + "/privatePayerBilling/invoice", {
                params: {
                    compID: props.user.comp_id,
                    custID: selectedCustomer?.cus_id ?? null,
                },
            })
            .then((response) => {
                setPrivatePayerInvoices(response.data);
            })
            .catch((error) => {
                console.log(error);
            });

        setLoading(false);
    };

    const taskRows = privatePayerTasks.map((task, index) => {
        return {
            id: index,
            tas_id: task.tas_id,
            tas_title: task.tas_title,
            pro_title: task.pro_title,
            cus_id: task.cus_id,
            cus_name: task.cus_name,
            empLocdate: convertTime(task.empLocdate, task.empLocate_timezone, moment.tz.guess()) ?? moment(task.empLocdate).format('MM/DD/YYYY hh:mm A'),
            checkoutEmpDate: convertTime(task.checkoutEmpDate, task.empLocate_timezone, moment.tz.guess()) ?? moment(task.checkoutEmpDate).format('MM/DD/YYYY hh:mm A'),
            taskHours: task.taskHours,
            taskRate: `$${task.taskRate}`,
            taskAmount: `$${task.taskAmount}`,
        };
    });

    const taskColumns = [
        { field: "id", headerName: "ID", width: 50 },
        { field: "tas_id", headerName: "Task ID", width: 75 },
        { field: "tas_title", headerName: "Task Title", width: 200 },
        { field: "pro_title", headerName: "Project Title", width: 200 },
        { field: "cus_name", headerName: "Client Name", width: 200 },
        { field: "empLocdate", headerName: "Task Start Date", width: 150 },
        { field: "checkoutEmpDate", headerName: "Task End Date", width: 150 },
        { field: "taskHours", headerName: "Task Hours", width: 75 },
        { field: "taskRate", headerName: "Task Rate", width: 75, align: 'right' },
        { field: "taskAmount", headerName: "Task Amount", width: 75, align: 'right' },
    ];

    // Group invoices by invoiceID
    const invoiceGroups = privatePayerInvoices.reduce((acc, invoice) => {
        if (!acc[invoice.invoiceID]) {
            acc[invoice.invoiceID] = {
                invoiceID: invoice.invoiceID,
                invoiceNumber: invoice.invoiceNumber,
                invoiceTotal: invoice.invoiceTotal,
                cus_name: invoice.cus_name,
                invoiceStatus: invoice.invoiceStatus,
                invoiceRaisedOn: invoice.invoiceRaisedOn,
                invoiceCreatedBy: invoice.emp_first_name + ' ' + invoice.emp_last_name,
                tasks: []
            };
        }
        acc[invoice.invoiceID].tasks.push(invoice);
        return acc;
    }, {});

    const invoiceRows = [];

    Object.values(invoiceGroups).forEach((invoiceGroup) => {
        // Parent invoice row
        invoiceRows.push({
            hierarchy: [invoiceGroup.invoiceNumber],
            id: `invoice-${invoiceGroup.invoiceNumber}`,
            invoiceID: invoiceGroup.invoiceID,
            invoiceNumber: invoiceGroup.invoiceNumber,
            invoiceTotal: `$${invoiceGroup.invoiceTotal.toFixed(2)}`,
            cus_name: invoiceGroup.cus_name,
            invoiceStatus: invoiceGroup.invoiceStatus,
            invoiceRaisedOn: moment(invoiceGroup.invoiceRaisedOn).format('MM/DD/YYYY'),
            invoiceCreatedBy: invoiceGroup.invoiceCreatedBy,
            isInvoiceRow: true,
        });

        // Child task rows
        invoiceGroup.tasks.forEach((task) => {
            invoiceRows.push({
                hierarchy: [invoiceGroup.invoiceNumber, `Task ID: ${task.tas_id}`],
                id: `task-${task.tas_id}`,
                invoiceID: invoiceGroup.invoiceID,
                invoiceNumber: invoiceGroup.invoiceNumber,
                tas_id: task.tas_id,
                tas_title: task.tas_title,
                isInvoiceRow: false,
            });
        });
    });

    // const invoiceRows = privatePayerInvoices.map((invoice, index) => {
    //     return {
    //         hierarchy: [invoice.invoiceID, invoice.tas_id],
    //         id: index,
    //         invoiceID: invoice.invoiceID,
    //         invoiceNumber: invoice.invoiceNumber,
    //         invoiceTotal: invoice.invoiceTotal,
    //         tas_id: invoice.tas_id,
    //         tas_title: invoice.tas_title,
    //         cus_name: invoice.cus_name,
    //         invoiceStatus: invoice.invoiceStatus,
    //         invoiceRaisedOn: moment(invoice.invoiceRaisedOn).format('MM/DD/YYYY'),
    //         invoiceCreatedBy: invoice.emp_first_name + ' ' + invoice.emp_last_name,
    //     };
    // });

    const invoiceColumns = [
        { field: "invoiceTotal", headerName: "Invoice Total", width: 100, align: 'right' },
        { field: "cus_name", headerName: "Invoice Client", width: 200 },
        { field: "invoiceStatus", headerName: "Invoice Status", width: 120 },
        { field: "invoiceRaisedOn", headerName: "Invoice Raised On", width: 150 },
        { field: "invoiceCreatedBy", headerName: "Created By", width: 150 },
        { field: "tas_title", headerName: "Task Title", width: 200 },
    ];

    const generateInvoices = async () => {
        if (!selectionModel || selectionModel.ids.size === 0) return;

        setIsGenerating(true);

        const selectedIDs = Array.from(selectionModel.ids);
        const selectedRows = selectedIDs.map(id => taskRows.find(r => r.id === id)).filter(Boolean);

        await axios
            .post(APIURL + `/privatePayerBilling/invoice`, {
                invoiceCompany: props.user.comp_id,
                invoiceCreatedBy: props.user.emp_id,
                tasks: selectedRows,
            })
            .then(function (response) {
                setAlertMessage('Invoices generated successfully!');
                setAlertType('success');
            })
            .catch(function (error) {
                console.log(error);
                setAlertMessage('An error occurred while generating invoices!');
                setAlertType('error');
            });

        setSelectionModel({
            type: 'include',
            ids: new Set(),
        });
        setIsGenerating(false);

        fetchData();
    };

    const sendInvoices = async () => {
        if (!invoiceSelectionModel || invoiceSelectionModel.ids.size === 0) return;

        setIsSending(true);

        const selectedIDs = Array.from(invoiceSelectionModel.ids);
        const selectedRows = selectedIDs.map(id => invoiceRows.find(r => r.id === id)).filter(Boolean);

        await axios
            .post(APIURL + `/privatePayerBilling/invoices/send`, {
                com_id: props.comp_id,
                emp_id: props.user.emp_id,
                invoices: selectedRows.map(r => r.invoiceID),
            })
            .then((response) => {
                console.log(response);
                setAlertMessage(`Invoices sent successfully! ${response.data.summary?.successful || 0} of ${response.data.summary?.total || 0} sent.`);
                setAlertType('success');
            })
            .catch((error) => {
                console.log(error);
                setAlertMessage("An error occurred while sending invoices! Please try again.");
                setAlertType("error");
            });

        setInvoiceSelectionModel({
            type: 'include',
            ids: new Set(),
        });
        setIsSending(false);

        fetchData();
    };

    const fetchReport = async (invoiceID) => {
        axios
            .get(APIURL + "/jsreport/privatePayerInvoice", {
                params: {
                    compID: props.user.comp_id,
                    invoiceID: invoiceID,
                },
                responseType: "arraybuffer",
                headers: {
                    Accept: "application/pdf",
                },
            })
            .then((response) => {
                openPDF(response, 'open');
            })
            .catch((error) => {
                console.error("Error fetching Private Payer Invoice:", error);
            });
    };

    const openPDF = (response, reportAction = "open") => {
        const file = new Blob([response.data], { type: "application/pdf" });
        const fileURL = URL.createObjectURL(file);

        if (reportAction === "download") {
            const link = document.createElement("a");
            link.href = fileURL;
            link.download = "Report-" + new Date() + ".pdf";
            link.click();
        } else {
            window.open(fileURL, "_blank");
        }
    };

    const SnackbarComponent = () => {
        return (
            <div>
                <Snackbar open={alertMessage ? true : false} autoHideDuration={2000} onClose={handleSnackbarAlertClose}>
                    <Alert severity={alertType} elevation={6} variant="filled">
                        {alertMessage}
                        <IconButton
                            sx={{ p: 0.25 }}
                            onClick={handleSnackbarAlertClose}
                        >
                            <CloseIcon />
                        </IconButton>
                    </Alert>
                </Snackbar>
            </div>
        );
    };

    const handleSnackbarAlertClose = (event, reason) => {
        if (reason === 'clickaway') {
            return;
        }
        setAlertMessage('');
        setAlertType('');
    };

    return (
        <div style={{ marginTop: 10, marginBottom: 10, width: '100%' }}>
            <div style={{ display: 'flex' }}>
                <Autocomplete
                    id="client-select"
                    options={customerList}
                    getOptionLabel={(option) => option.cus_name}
                    onChange={(event, value) => setSelectedCustomer(value)}
                    renderOption={(props, option) => (
                        <li {...props} key={option.cus_id}>
                            {option.cus_name}
                        </li>
                    )}
                    renderInput={(params) => <TextField {...params} label="Select Client" />}
                    sx={{ width: 250, marginRight: 2 }}
                    size="small"
                />

                <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DateRangePicker
                        label='Task Date Range'
                        defaultValue={shortcutsItems[0].getValue()}
                        onChange={(newValue) => {
                            setFromDate(newValue[0] ? new Date(newValue[0]) : null);
                            setToDate(newValue[1] ? new Date(newValue[1]) : null);
                        }}
                        slotProps={{
                            shortcuts: {
                                items: shortcutsItems,
                            },
                            textField: { size: 'small', style: { width: 275, marginRight: 20 } },
                        }}
                    />
                </LocalizationProvider>

                <Button
                    onClick={() => { fetchData(); }}
                    variant="contained"
                    disabled={!moment(toDate).isValid() || !moment(fromDate).isValid()}
                >
                    SEARCH
                </Button>
            </div>
            <div style={{ marginTop: 10, marginBottom: 10 }}>
                <Accordion
                    expanded={isAccordion1Open}
                    onChange={(e, expanded) => setIsAccordion1Open(expanded)}
                >
                    <AccordionSummary
                        expandIcon={<ExpandMoreIcon />}
                        sx={summarySx}
                    >
                        <Typography component="span">Tasks yet to be invoiced</Typography>
                    </AccordionSummary>
                    <AccordionDetails sx={detailsSx}>
                        <div style={{ height: gridHeight }}>
                            <DataGridPro
                                initialState={{ sorting: { sortModel: [{ field: "checkoutEmpDate", sort: "desc" }] } }}
                                getRowId={(row) => row.id}
                                rows={taskRows}
                                columns={taskColumns}
                                checkboxSelection
                                rowSelectionModel={selectionModel}
                                onRowSelectionModelChange={(newSelection) => {
                                    if (newSelection.type === 'exclude') {
                                        setSelectionModel({
                                            type: 'include',
                                            ids: new Set(taskRows.map(r => r.id)),
                                        })
                                    } else {
                                        setSelectionModel(newSelection);
                                    }
                                }}
                                disableRowSelectionOnClick
                                showToolbar
                                loading={loading}
                                slots={{
                                    toolbar: () => {
                                        return (
                                            <div>
                                                <div style={{ display: 'flex', padding: 5, marginBottom: -50 }}>
                                                    <Button
                                                        variant="contained"
                                                        onClick={() => { generateInvoices(); }}
                                                        disabled={selectionModel.ids.size === 0 || isGenerating}
                                                    >
                                                        Generate Invoice/s
                                                    </Button>
                                                </div>
                                                <DefaultToolbar></DefaultToolbar>
                                            </div>
                                        );
                                    },
                                }}
                                sx={{
                                    '& .MuiDataGrid-columnHeaderTitle': {
                                        whiteSpace: 'normal',
                                        lineHeight: 1.2,
                                        textAlign: 'left',
                                        wordBreak: 'break-word',
                                        maxWidth: '100%',
                                    },
                                }}
                            />
                        </div>
                    </AccordionDetails>
                </Accordion>
            </div>
            <div>
                <Accordion
                    expanded={isAccordion2Open}
                    onChange={(e, expanded) => setIsAccordion2Open(expanded)}
                >
                    <AccordionSummary
                        expandIcon={<ExpandMoreIcon />}
                        sx={summarySx}
                    >
                        <Typography component="span">Invoices</Typography>
                    </AccordionSummary>
                    <AccordionDetails sx={detailsSx}>
                        <div style={{ height: gridHeight }}>
                            <DataGridPro
                                initialState={{ sorting: { sortModel: [{ field: "invoiceRaisedOn", sort: "desc" }] } }}
                                getRowId={(row) => row.id}
                                rows={invoiceRows}
                                columns={invoiceColumns}
                                checkboxSelection={true}
                                isRowSelectable={(params) => params.row.isInvoiceRow === true && params.row.invoiceStatus === 'Invoice Raised'}
                                onRowSelectionModelChange={(newSelection) => {
                                    if (newSelection.type === 'exclude') {
                                        setInvoiceSelectionModel({
                                            type: 'include',
                                            ids: new Set(invoiceRows.map(r => r.id)),
                                        })
                                    } else {
                                        setInvoiceSelectionModel(newSelection);
                                    }
                                }}
                                rowSelectionModel={invoiceSelectionModel}
                                disableRowSelectionOnClick
                                showToolbar
                                loading={loading}
                                slots={{
                                    toolbar: () => {
                                        return (
                                            <div>
                                                <div style={{ display: 'flex', padding: 5, marginBottom: -50, alignItems: 'center' }}>
                                                    <Button
                                                        variant="contained"
                                                        onClick={() => { sendInvoices(); }}
                                                        disabled={invoiceSelectionModel.ids.size === 0 || isSending}
                                                    >
                                                        Send Invoice/s
                                                    </Button>
                                                    {isSending &&
                                                        <CircularProgress size="30px" sx={{ marginLeft: 2 }} />
                                                    }
                                                </div>
                                                <DefaultToolbar></DefaultToolbar>
                                            </div>
                                        );
                                    },
                                }}
                                groupingColDef={{
                                    headerName: 'Invoices',
                                    renderCell: (params) => {
                                        const isInvoiceGroup = params.rowNode?.type === 'group' && params.rowNode?.depth === 0;
                                        return (
                                            <div style={{ display: 'flex' }}>
                                                <GridTreeDataGroupingCell {...params} />
                                                {isInvoiceGroup && (
                                                    <Tooltip title="Print Invoice">
                                                        <IconButton
                                                            color="primary"
                                                            onClick={(e) => { e.stopPropagation(); fetchReport(params.row.invoiceID); }}
                                                        >
                                                            <ReceiptIcon fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                )}
                                            </div>
                                        );
                                    },
                                }}
                                treeData
                                getTreeDataPath={(row) => row.hierarchy}
                                sx={{
                                    '& .MuiDataGrid-columnHeaderTitle': {
                                        whiteSpace: 'normal',
                                        lineHeight: 1.2,
                                        textAlign: 'left',
                                        wordBreak: 'break-word',
                                        maxWidth: '100%',
                                    },
                                }}
                            />
                        </div>
                    </AccordionDetails>
                </Accordion>
            </div>
            {alertMessage && SnackbarComponent()}
        </div>
    );
};